/**
 * Sprint 89 — Lo que la lista de pedidos dice de cada pedido, y qué falta hacerle.
 *
 * Nace de una contradicción que el negocio fotografió: la misma fila decía **"Pago pendiente"** en
 * la columna de estado y **"✓ Ya pagado"** en la de acciones.
 *
 * > *"analiza y dime si ves la incongruencia… ¿quién le reintegra la pérdida al dueño del
 * > negocio?"*
 *
 * ## Qué pasaba de verdad
 *
 * La etiqueta salía de un diccionario que sólo miraba `Pedido.estado`, y ahí `'abierto'` estaba
 * traducido como *"Pago pendiente"*. Pero `estado` **no habla del pago**: habla de dónde va el
 * pedido en la cocina. Un domicilio al que ya se le registró el pago sigue `'abierto'` hasta que
 * alguien lo manda a cocina — `adjuntarComprobante` y `confirmarPagoVerificado` mueven
 * `estadoPago`, no `estado`.
 *
 * Y ahí estaba el daño real, que es más grave que el texto: **el pedido quedaba atorado**. El modal
 * de cobro hace dos llamadas seguidas (registrar el pago, y luego mandar a cocina); si la segunda
 * falla o alguien cierra la ventana en medio, el pedido queda **pagado y abierto**, y desde la
 * lista no había forma de sacarlo de ahí: "Cobrar" no aparece (ya tiene pago), "Comprobante"
 * tampoco (ya está pagado) y no existía ningún "mandar a cocina". Dinero cobrado, comida que nunca
 * se preparó, y una etiqueta diciendo que faltaba el pago.
 *
 * Nadie le reintegra eso al negocio. Por eso este módulo no sólo arregla el texto: **devuelve la
 * acción que falta**, para que ningún pedido pueda quedarse sin puerta de salida.
 */

export interface PedidoParaEtiqueta {
  estado: string;
  tipo?: string;
  /** ¿Existe un `Pago` registrado? (Sprint 88) */
  yaTienePago?: boolean;
  /** Sprint 137 — Se acordó cobrarlo al entregar. */
  pagaAlEntregar?: boolean | null;
}

/** Lo que la fila necesita para pintarse y para ofrecer lo que falta. */
export interface EtiquetaEstado {
  texto: string;
  /** Clases de color. Ámbar = falta algo por hacer; verde = va bien; rojo = muerto. */
  tono: string;
  /**
   * Lo que le falta a este pedido, si es que le falta algo **y se puede hacer desde la lista**.
   * `null` cuando la pelota está en otra cancha (la cocina, el repartidor).
   */
  accion: 'enviar_cocina' | 'registrar_pago' | null;
}

const AMBAR = 'bg-amber-100 text-amber-700';
const NARANJA = 'bg-orange-100 text-orange-700';
const VERDE = 'bg-green-100 text-green-700';
const AZUL = 'bg-blue-100 text-blue-700';
const GRIS = 'bg-slate-100 text-slate-700';
const ROJO = 'bg-red-100 text-red-700';

export function etiquetaEstadoPedido(p: PedidoParaEtiqueta): EtiquetaEstado {
  if (p.estado === 'cancelado') return { texto: 'Cancelado', tono: ROJO, accion: null };
  if (p.estado === 'pagado') return { texto: 'Pagado', tono: GRIS, accion: null };

  if (p.estado === 'abierto') {
    /**
     * El caso de la captura. Se nombra **el pendiente real**, no el que se suponía: el dinero ya
     * entró; lo que falta es que alguien lo mande a hacer.
     */
    if (p.yaTienePago === true) {
      return { texto: 'Pagado · falta mandarlo a cocina', tono: AMBAR, accion: 'enviar_cocina' };
    }
    /*
      Sprint 137 — "Falta el pago" sobre un contra entrega es falso, y además invita a cobrarlo
      dos veces: quien lee eso a las nueve de la noche le pide el dinero a un cliente que
      todavía no ha recibido su comida. Lo que falta aquí es mandarlo a la parrilla.
    */
    if (p.tipo === 'delivery' && p.pagaAlEntregar === true) {
      return { texto: 'Se cobra al entregar', tono: AMBAR, accion: 'enviar_cocina' };
    }
    // Un domicilio abierto sin pago sí está esperando el dinero del cliente.
    if (p.tipo === 'delivery') {
      return { texto: 'Falta el pago', tono: AMBAR, accion: 'registrar_pago' };
    }
    // Una mesa abierta no espera nada: está consumiendo. Llamarle "pago pendiente" es alarmismo.
    return { texto: 'Cuenta abierta', tono: GRIS, accion: null };
  }

  if (p.estado === 'pendiente_cocina' || p.estado === 'cocina') {
    return { texto: 'En cocina', tono: NARANJA, accion: null };
  }
  if (p.estado === 'listo') return { texto: 'Listo', tono: VERDE, accion: null };
  if (p.estado === 'entregado') return { texto: 'Entregado', tono: AZUL, accion: null };

  return { texto: p.estado, tono: GRIS, accion: null };
}
